import React, { useState, Component } from 'react';
import { ArrowLeftIcon, CalendarIcon, StickyNoteIcon, PhoneIcon, MailIcon, MapPinIcon, EditIcon, HashIcon, CopyIcon, CheckIcon, ListTodoIcon, UserPlusIcon, PlusIcon } from 'lucide-react';
import { Customer } from '../components/CustomerCard';
import { Task, TaskCard } from '../components/TaskCard';
import { Button } from '../components/Button';
import { motion } from 'framer-motion';
interface CustomerDetailProps {
  customer: Customer;
  tasks: Task[];
  onBack: () => void;
  onEdit: (customer: Customer) => void;
  onEditTask: (task: Task) => void;
  onDeleteTask: (task: Task) => void;
  onAddTask: (customerId: string) => void;
}
export function CustomerDetail({
  customer,
  tasks,
  onBack,
  onEdit,
  onEditTask,
  onDeleteTask,
  onAddTask
}: CustomerDetailProps) {
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [copiedSecondaryPhone, setCopiedSecondaryPhone] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<'all' | Task['status']>('all');
  const copyToClipboard = async (text: string, type: 'phone' | 'address' | 'secondaryPhone') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'phone') {
        setCopiedPhone(true);
        setTimeout(() => setCopiedPhone(false), 2000);
      } else if (type === 'address') {
        setCopiedAddress(true);
        setTimeout(() => setCopiedAddress(false), 2000);
      } else if (type === 'secondaryPhone') {
        setCopiedSecondaryPhone(true);
        setTimeout(() => setCopiedSecondaryPhone(false), 2000);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  const customerTasks = tasks.filter(t => t.customerId === customer.id);
  const activeTasks = customerTasks.filter(t => t.status === 'in-progress').length;
  const completedTasks = customerTasks.filter(t => t.status === 'done').length;
  const plannedTasks = customerTasks.filter(t => t.status === 'planned').length;
  const pendingTasks = customerTasks.filter(t => t.status === 'pending').length;
  const filteredTasks = selectedStatus === 'all' ? customerTasks : customerTasks.filter(t => t.status === selectedStatus);
  return <div data-id="element-1261" data-mp="zib5pf1784i2">
      <div className="flex items-center gap-4 mb-8" data-id="element-1262" data-mp="zj44nttx1ppn">
        <button onClick={onBack} className="p-2 text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition-all" aria-label="Tilbage" data-id="element-1263" data-mp="1wdqprm12hav">
          <ArrowLeftIcon className="w-5 h-5" data-id="element-1264" data-mp="bubdeao8o61d" />
        </button>
        <div className="flex-1" data-id="element-1265" data-mp="brymh80i6lc6">
          <h1 className="text-4xl font-bold text-stone-900" data-id="element-1266" data-mp="gki1m9eu9oj3">{customer.name}</h1>
          <div className="flex items-center gap-2 mt-2" data-id="element-1267" data-mp="gml25ndmb022">
            <HashIcon className="w-4 h-4 text-teal-600" data-id="element-1268" data-mp="3f9yzx2otvix6" />
            <p className="text-teal-600 font-medium" data-id="element-1269" data-mp="3k9ldjd8kj16i">{customer.customerId}</p>
          </div>
        </div>
        <div className="flex gap-2" data-id="element-1270" data-mp="3cqvkwxdz7ktw">
          <Button onClick={() => onAddTask(customer.id)} variant="primary" className="flex items-center gap-2" data-id="element-1271" data-mp="3cptwzsbv33ur">
            <PlusIcon className="w-4 h-4" data-id="element-1272" data-mp="32rmugeibkoca" />
            Tilføj opgave
          </Button>
          <Button onClick={() => onEdit(customer)} variant="secondary" className="flex items-center gap-2" data-id="element-1273" data-mp="2xtbl04vxsful">
            <EditIcon className="w-4 h-4" data-id="element-1274" data-mp="2tt12jsqyjwyu" />
            Rediger
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8" data-id="element-1275" data-mp="2ozgfuwutepey">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }} className="bg-white rounded-xl p-6 shadow-sm border border-stone-100" data-id="element-1276" data-mp="2k0ve2szwzxoe">
          <div className="flex items-center gap-3 mb-2" data-id="element-1277" data-mp="262f8549vmb7b">
            <div className="p-2 bg-blue-100 rounded-lg" data-id="element-1278" data-mp="2us1d1d584avk">
              <CalendarIcon className="w-5 h-5 text-blue-700" data-id="element-1279" data-mp="2utbbi3fznl2q" />
            </div>
            <h3 className="text-stone-600 text-sm font-medium" data-id="element-1280" data-mp="2zr4j90wxwn54">
              Opgaver i alt
            </h3>
          </div>
          <p className="text-3xl font-bold text-stone-900" data-id="element-1281" data-mp="2ly54j14zewym">
            {customerTasks.length}
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.05
      }} className="bg-white rounded-xl p-6 shadow-sm border border-stone-100" data-id="element-1282" data-mp="2c2bimr5dhr46">
          <div className="flex items-center gap-3 mb-2" data-id="element-1283" data-mp="2738q266kxdd3">
            <div className="p-2 bg-amber-100 rounded-lg" data-id="element-1284" data-mp="19qv0sddgqz5">
              <CalendarIcon className="w-5 h-5 text-amber-700" data-id="element-1285" data-mp="186031n5b60r" />
            </div>
            <h3 className="text-stone-600 text-sm font-medium" data-id="element-1286" data-mp="5asw7eqund3r">
              Aktive opgaver
            </h3>
          </div>
          <p className="text-3xl font-bold text-stone-900" data-id="element-1287" data-mp="1pra9vvdgwoz1">{activeTasks}</p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: 0.1
      }} className="bg-white rounded-xl p-6 shadow-sm border border-stone-100" data-id="element-1288" data-mp="1lq856z0s7kpo">
          <div className="flex items-center gap-3 mb-2" data-id="element-1289" data-mp="24hr6n1f5qqpl">
            <div className="p-2 bg-emerald-100 rounded-lg" data-id="element-1290" data-mp="2iibsy7ka74nu">
              <CalendarIcon className="w-5 h-5 text-emerald-700" data-id="element-1291" data-mp="2ihisfobx1be5" />
            </div>
            <h3 className="text-stone-600 text-sm font-medium" data-id="element-1292" data-mp="2dkikt8zf40kx">
              Afsluttede opgaver
            </h3>
          </div>
          <p className="text-3xl font-bold text-stone-900" data-id="element-1293" data-mp="o6dik2a8k2qd">{completedTasks}</p>
        </motion.div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      delay: 0.15
    }} className="bg-white rounded-xl p-8 shadow-sm border border-stone-100" data-id="element-1294" data-mp="j7kaipdpx3xl">
        <h2 className="text-2xl font-semibold text-stone-900 mb-6" data-id="element-1295" data-mp="2x7qbmtwc8knt">
          Kundeinformation
        </h2>

        <div className="grid md:grid-cols-2 gap-6" data-id="element-1296" data-mp="32739sasnte5p">
          {customer.address && <div className="flex items-start gap-4" data-id="element-1297" data-mp="374uzcy5u0o6q">
              <div className="p-3 bg-teal-100 rounded-lg" data-id="element-1298" data-mp="373jyp77onwju">
                <MapPinIcon className="w-5 h-5 text-teal-700" data-id="element-1299" data-mp="3bvuwa3eyb698" />
              </div>
              <div className="flex-1" data-id="element-1300" data-mp="3gvpme4qwjrt8">
                <p className="text-sm font-medium text-stone-500 mb-1" data-id="element-1301" data-mp="3guobq1dst7c1">
                  Adresse
                </p>
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(customer.address)}`} target="_blank" rel="noopener noreferrer" className="text-stone-900 hover:text-teal-600 transition-colors underline" data-id="element-1302" data-mp="3kuq2hfhzwgcj">
                  {customer.address}
                </a>
              </div>
              <button onClick={() => copyToClipboard(customer.address, 'address')} className="p-2 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Kopiér adresse" data-id="element-1303" data-mp="159yl0pkpz8nk">
                {copiedAddress ? <CheckIcon className="w-5 h-5 text-teal-600" data-id="element-1304" data-mp="1szjg26u5dwd7" /> : <CopyIcon className="w-5 h-5" data-id="element-1305" data-mp="1o4vsqbavr807" />}
              </button>
            </div>}

          {customer.phone && <div className="flex items-start gap-4" data-id="element-1306" data-mp="2gnj95dexlyuy">
              <div className="p-3 bg-teal-100 rounded-lg" data-id="element-1307" data-mp="2gp41cwdrx5pn">
                <PhoneIcon className="w-5 h-5 text-teal-700" data-id="element-1308" data-mp="2bv9l62xdn8vb" />
              </div>
              <div className="flex-1" data-id="element-1309" data-mp="1k87v5be9pe2e">
                <p className="text-sm font-medium text-stone-500 mb-1" data-id="element-1310" data-mp="1k758xgrw3zst">
                  Telefon
                </p>
                <p className="text-stone-900 font-medium" data-id="element-1311" data-mp="1p1j6npu0yj0n">{customer.phone}</p>
              </div>
              <button onClick={() => copyToClipboard(customer.phone, 'phone')} className="p-2 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Kopiér telefonnummer" data-id="element-1312" data-mp="1a8h1td1g153w">
                {copiedPhone ? <CheckIcon className="w-5 h-5 text-teal-600" data-id="element-1313" data-mp="27vy61gk90dp0" /> : <CopyIcon className="w-5 h-5" data-id="element-1314" data-mp="1ty9hnyqz2of7" />}
              </button>
            </div>}

          {customer.email && <div className="flex items-start gap-4" data-id="element-1315" data-mp="ij4giz6hoxxp">
              <div className="p-3 bg-teal-100 rounded-lg" data-id="element-1316" data-mp="ilgxpa4fb49p">
                <MailIcon className="w-5 h-5 text-teal-700" data-id="element-1317" data-mp="mm0prza53hoz" />
              </div>
              <div className="flex-1" data-id="element-1318" data-mp="f0x57jg5z591">
                <p className="text-sm font-medium text-stone-500 mb-1" data-id="element-1319" data-mp="ezvmqtwtjgha">Email</p>
                <a href={`mailto:${customer.email}`} className="text-stone-900 hover:text-teal-600 transition-colors" data-id="element-1320" data-mp="a1ka6bf52rvd">
                  {customer.email}
                </a>
              </div>
            </div>}

          {customer.secondaryContactName && <div className="flex items-start gap-4" data-id="element-1321" data-mp="3sgmfz5te4tsg">
              <div className="p-3 bg-teal-100 rounded-lg" data-id="element-1322" data-mp="3nhryc775tb9x">
                <UserPlusIcon className="w-5 h-5 text-teal-700" data-id="element-1323" data-mp="3nff9da2zi0nd" />
              </div>
              <div className="flex-1" data-id="element-1324" data-mp="6234bgvsvspo">
                <p className="text-sm font-medium text-stone-500 mb-1" data-id="element-1325" data-mp="1ablxtmsstam">
                  Ekstra kontaktperson
                </p>
                <p className="text-stone-900 font-medium" data-id="element-1326" data-mp="39otqvmhfzsq0">
                  {customer.secondaryContactName}
                </p>
                {customer.secondaryContactPhone && <div className="flex items-center gap-2 mt-1" data-id="element-1327" data-mp="1lvo38k4up3mi">
                    <p className="text-stone-600 text-sm" data-id="element-1328" data-mp="1htt2bgqu0s99">
                      {customer.secondaryContactPhone}
                    </p>
                    <button onClick={() => copyToClipboard(customer.secondaryContactPhone!, 'secondaryPhone')} className="p-1 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Kopiér telefonnummer" data-id="element-1329" data-mp="1ctn5lm5pgfkb">
                      {copiedSecondaryPhone ? <CheckIcon className="w-3 h-3 text-teal-600" data-id="element-1330" data-mp="p66o7ejza585" /> : <CopyIcon className="w-3 h-3" data-id="element-1331" data-mp="k4zdp7qgarxs" />}
                    </button>
                  </div>}
              </div>
            </div>}
        </div>

        {customer.notes && <div className="mt-6 pt-6 border-t border-stone-200" data-id="element-1332" data-mp="553pu31na95h">
            <div className="flex items-start gap-4" data-id="element-1333" data-mp="a12frv2mfz80">
              <div className="p-3 bg-amber-100 rounded-lg" data-id="element-1334" data-mp="f1705w6nhe3i">
                <StickyNoteIcon className="w-5 h-5 text-amber-700" data-id="element-1335" data-mp="f1q7kiyock3a" />
              </div>
              <div className="flex-1" data-id="element-1336" data-mp="jyyuewurpcav">
                <p className="text-sm font-medium text-stone-500 mb-2" data-id="element-1337" data-mp="jxxfuiejm7oe">Noter</p>
                <p className="text-stone-700 leading-relaxed" data-id="element-1338" data-mp="3ijqjg97cp9m6">
                  {customer.notes}
                </p>
              </div>
            </div>
          </div>}
      </motion.div>

      {/* Customer Tasks Section */}
      {customerTasks.length > 0 && <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      delay: 0.2
    }} className="mt-8" data-id="element-1339" data-mp="127dgu2p799z">
          <div className="flex items-center justify-between mb-6" data-id="element-1340" data-mp="2woi70h0syulp">
            <div className="flex items-center gap-3" data-id="element-1341" data-mp="2rr9mi9s6meha">
              <div className="p-2 bg-amber-100 rounded-lg" data-id="element-1342" data-mp="2rqge74dkrgx1">
                <ListTodoIcon className="w-5 h-5 text-amber-700" data-id="element-1343" data-mp="36fw3efxgjeam" />
              </div>
              <h2 className="text-2xl font-semibold text-stone-900" data-id="element-1344" data-mp="31fzxgb579csf">
                Opgaver for {customer.name}
              </h2>
            </div>
          </div>

          {/* Status Filter Buttons */}
          <div className="flex flex-wrap gap-2 mb-6" data-id="element-1345" data-mp="28snk8rkxihbs">
            <button onClick={() => setSelectedStatus('all')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${selectedStatus === 'all' ? 'bg-stone-900 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1346" data-mp="28tyqqz5re36l">
              Alle ({customerTasks.length})
            </button>
            <button onClick={() => setSelectedStatus('in-progress')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${selectedStatus === 'in-progress' ? 'bg-amber-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1347" data-mp="3u45i6xw971s9">
              I gang ({activeTasks})
            </button>
            <button onClick={() => setSelectedStatus('done')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${selectedStatus === 'done' ? 'bg-emerald-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1348" data-mp="k8vwn6lkjgz">
              Afsluttet ({completedTasks})
            </button>
            <button onClick={() => setSelectedStatus('planned')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${selectedStatus === 'planned' ? 'bg-blue-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1349" data-mp="fgzz77je6fbv">
              Planlagt ({plannedTasks})
            </button>
            <button onClick={() => setSelectedStatus('pending')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${selectedStatus === 'pending' ? 'bg-purple-600 text-white shadow-sm' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`} data-id="element-1350" data-mp="3kh02hwa396md">
              Afventende ({pendingTasks})
            </button>
          </div>

          {/* Tasks Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1351" data-mp="71tz4wovyo2q">
            {filteredTasks.map((task, index) => <TaskCard key={task.id} task={task} index={index} onClick={() => onEditTask(task)} onDelete={onDeleteTask} data-id="element-1352" data-mp="23se467wgeom" data-mpi={index} />)}
          </div>

          {filteredTasks.length === 0 && <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200" data-id="element-1353" data-mp="psv6o942nrxw">
              <ListTodoIcon className="w-12 h-12 text-stone-300 mx-auto mb-3" data-id="element-1354" data-mp="l0adn9szpivf" />
              <p className="text-stone-600 font-medium" data-id="element-1355" data-mp="23omu4x5mtgd6">
                Ingen opgaver i denne kategori
              </p>
              <p className="text-sm text-stone-500 mt-1" data-id="element-1356" data-mp="28nzd36tdu3ah">
                Prøv at vælge en anden status
              </p>
            </div>}
        </motion.div>}
    </div>;
}